import { AnnotationMetadata } from '@/features/annotation'
import { ResponseThread } from '@/schema'

export enum TflStatus {
  NEW = 'new',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  QUERY_RAISED = 'query_raised',
  UNDER_REVISION = 'under_revision',
}

export enum TflKind {
  TABLE = 'table',
  FIGURE = 'figure',
  LISTING = 'listing',
}

export enum UserRole {
  OWNER_AUTHOR = 'owner-author',
  AUTHOR_REVIEWER = 'author-reviewer',
  AUTHOR = 'author',
  REVIEWER = 'reviewer',
  VIEWER = 'viewer',
}

export enum ReviewerStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export interface TflDocumentVersion {
  version_number: number
  file_size: number
  content_type: string
  uploaded_at: string
}

export interface TflDocumentMetadata {
  document_id: string
  display_name: string
  filename: string
  document_type: string
  status: string
  deliverable_set_id: string
  version_number: number
  version_id: string
  signed_blob_url: string
  expires_at: string
  reviewers: Reviewer[]
  created_by: string
  owner: Owner
  is_bookmarked?: boolean
}

export interface ApiResponseMetadata {
  correlation_id: string
  request_id: string
  timestamp: string
  api_version: string
  service_version: string
  audit_trail_id: string | null
  compliance_level: string
  processing_time_ms: number
  rate_limit_remaining: number | null
  rate_limit_reset: string | null
}

export interface TflDocumentApiResponse {
  success: string
  data: TflDocumentMetadata[]
  metadata: ApiResponseMetadata
  pagination: unknown
  links: unknown
  warnings: unknown
}

export interface Reviewer {
  id: string
  first_name: string
  last_name: string
  avatar_url: string
  status: ReviewerStatus
}

export interface Owner {
  id: string
  first_name: string
  last_name: string
  email: string
  avatar_url?: string | null
  status?: string | null
}

export interface TflMeta {
  id: string
  dsId: string
  displayName: string
  fileName: string
  status: TflStatus
  kind: TflKind
  signedUrl: string
  version: number
  version_id: string
  expiresAt: string
  documentType?: string
  reviewers: Reviewer[]
  created_by?: string
  owner?: Owner
  is_bookmarked?: boolean
}

export interface TflTocItem {
  id: string
  name: string
  status: TflStatus
  createdAt: string
  kind?: TflKind
}

export type EditorModeType = 'plain' | 'markdown'

export type CreateCommentRequest = {
  content: string
  document_version_id: string
  annotation?: AnnotationMetadata
  parent_comment_id?: string
  quote_comment_id?: string
}

export type CreateCommentResponse = {
  success: boolean
  data: {
    id: string
    content: string
    document_version_id: string
    created_at: string
    updated_at: string
    annotation?: AnnotationMetadata
  }
}

export type updateComment = {
  content: string
  mentioned_user_ids: string[]
  updated_at: string
  updated_by: string
}

export type updateThreadStatusRequest = {
  status: 'resolved' | 'open'
  annotation?: AnnotationMetadata
}

export type UpdateThreadStatusResponse = {
  success: boolean
  data: ResponseThread[]
}

export type GetThreadCommentsResponse = {
  success: boolean
  data: ResponseThread[]
}

export type CreateReplyRequest = {
  content: string
  parent_comment_id: string
}

export type Thread = {
  content: string
  created_at: string
  created_by: string
  id: string
  parent_comment_id: string
}

export type CreateReplyResponse = {
  success: boolean
  data: Thread[]
}

export type UpdateCommentRequest = {
  content: string
  mentioned_user_ids?: string[]
}

export type UpdateCommentResponse = {
  success: boolean
  data: ResponseThread[]
}

export type GetThreadReadResponse = {
  success: boolean
  data: {
    thread_id: string
    participant_id: string
    last_read_at: string | null
    is_read: boolean
  }
}

export type TCommentStatus = 'open' | 'resolved' | 'all'

export type APIPayloadListComments = {
  filter_by: {
    document_version_id?: string[]
    status?: TCommentStatus[]
    deliverable_set_name?: string[]
    actionable_type?: string[]
    [key: string]: string[] | undefined
  }
  limit: number
  offset: number
  sort_by: string
  q?: string
}

export type TransferCommentPayLoad = {
  destination_document_version_id: string
  source_document_version_id: string
  transfer_comments: { id: string }[]
}

export type CommentListResponse = {
  data: ResponseThread[]
  success: boolean
  pagination: {
    page: number
    page_size: number
    total_pages: number
    total_items: number
    has_next: boolean
    has_previous: boolean
  }
}

export interface BookmarkResponse {
  data: Array<{
    document_id: string
    user_id: string
    is_bookmarked: boolean
  }>
  success: boolean
  pagination: {
    page: number
    page_size: number
    total_pages: number
    total_items: number
    has_next: boolean
    has_previous: boolean
  }
}

export interface PendingAnnotationState {
  metadata: AnnotationMetadata | null
  showBubble: boolean
  bubblePosition: { top: number; left: number } | null
}

export enum ECommentStatus {
  ALL = 'all',
  OPEN = 'open',
  RESOLVED = 'resolved',
}

export enum EStickyPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
}
